// glossary-definitions.js
// Centralised glossary for SRT Calculator fields

export const GLOSSARY = {
   // === SRT Settings ===
   srtTarget: {
      section: 'SRT Settings',
      inputType: 'user',
      title: 'Target SRT',
      short: 'Desired rollover threshold (g) target.',
      long: 'Minimum acceptable Static Rollover Threshold (SRT) for compliance. Common values are 0.35g (General) and 0.40g (Dangerous Goods).  ',
   },

   // === Vehicle Details ===
   vehicleType: {
      section: 'Vehicle Details',
      inputType: 'user',
      title: 'Vehicle Type',
      short: 'Type of unit being assessed (e.g. truck, semi-trailer).',
      long: 'Specifies the structural configuration of the vehicle or trailer unit being assessed. This influences axle placement, load assumptions, and SRT logic.',
   },
   frontAxles: {
      section: 'Vehicle Details',
      inputType: 'user',
      title: 'Front Axles',
      short: 'Number of front axle groups on the unit.',
      long: `
      Number of axles positioned at the front of the unit. Typically 1 (e.g. steer axle), but can be 2 in twin-steer trucks. <br>
      <h6 class="mt-4">Allowable Front Axles</h6>
         <div class="table-responsive-sm my-3">
         <table class="table table-bordered table-sm w-auto align-middle">
            <thead class="table-light">
               <tr>
               <th scope="col">Vehicle Type</th>
               <th scope="col">Front Axles (min–max)</th>
               </tr>
            </thead>
            <tbody>
               <tr><td>Truck</td><td>1 – 2</td></tr>
               <tr><td>Dog Trailer</td><td>1 – 4</td></tr>
            </tbody>
         </table>
         </div>
      `,
   },
   rearAxles: {
      section: 'Vehicle Details',
      inputType: 'user',
      title: 'Rear Axles',
      short: 'Number of rear axle groups on the unit.',
      long: `Number of axles at the rear of the unit. For trucks, this is the drive axle group. For trailers, it’s the main load-carrying group. <br>
      <h6 class="mt-4">Allowable Rear Axles</h6>
      <div class="table-responsive-sm my-3">
      <table class="table table-bordered table-sm w-auto align-middle">
         <thead class="table-light">
            <tr>
            <th scope="col">Vehicle Type</th>
            <th scope="col">Rear Axles (min–max)</th>
            </tr>
         </thead>
         <tbody>
            <tr><td>Truck</td><td>1 – 4</td></tr>
            <tr><td>Dog Trailer</td><td>1 – 4</td></tr>
            <tr><td>Semi-Trailer</td><td>1 – 4</td></tr>
            <tr><td>Pig Trailer</td><td>1 – 4</td></tr>
            <tr><td>Jinker Trailer</td><td>1 – 4</td></tr>
            <tr><td>Low Loader</td><td>3 – 6</td></tr>
         </tbody>
      </table>
      </div>
`,
   },

   // === Tyres ===
   'f-tyre-size': {
      section: 'Tyres',
      inputType: 'user',
      title: 'Front Tyre Size',
      short: 'Nominal rim size in inches (e.g. 22.5).',
      long: 'Diameter of front tyres, typically 17.5, 19.5 or 22.5. Used to estimate unsprung mass and axle height, axle mass, and track width (in combination with tyre configuration). Low-profile tyres are not separately modelled.',
   },
   'r-tyre-size': {
      section: 'Tyres',
      inputType: 'user',
      title: 'Rear Tyre Size',
      short: 'Nominal rim size in inches (e.g. 22.5).',
      long: 'Diameter of rear tyres, typically 17.5, 19.5 or 22.5. Used to estimate unsprung mass and axle height, axle mass, and track width (in combination with tyre configuration). Low-profile tyres are not separately modelled.',
   },
   'f-tyre-config': {
      section: 'Tyres',
      inputType: 'user',
      title: 'Front Tyre Configuration',
      short: 'Tyre type: Single, Wide Single or Dual.',
      long: 'Defines the number and width of tyres per wheel end at front. Affects track width, axle mass, and vertical tyre stiffness of the axle group.',
   },
   'r-tyre-config': {
      section: 'Tyres',
      inputType: 'user',
      title: 'Rear Tyre Configuration',
      short: 'Tyre setup per axle (single, wide, dual, row of 8).',
      long: 'Defines the number and width of tyres per wheel end on rear group. Affects track width, axle mass, and vertical tyre stiffness of the axle group.',
   },
   'f-max-contact-width': {
      section: 'Tyres',
      inputType: 'calculated',
      title: 'Front Ground Contact Width',
      short: 'Width between outermost front tyres.',
      long: 'Measured between the outer edges of the front tyres at ground level. Used to derive front axle track width and suspension moment arms.',
      figures: ['/img/track-width-calc3.svg'],
   },
   'r-max-contact-width': {
      section: 'Tyres',
      inputType: 'calculated',
      title: 'Rear Ground Contact Width',
      short: 'Width between outermost rear tyres.',
      long: 'Measured at ground contact, used to compute rear track width and axle geometry.',
   },

   // === Axle Loads ===
   'f-gross-mass': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Front Gross Mass',
      short: 'Loaded front axle group mass (kg).',
      long: 'Gross operating mass carried on the front axle group when fully laden. Includes tare and payload. A warning is shown if limits are exceeded.',
   },
   'r-gross-mass': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Rear Gross Mass',
      short: 'Loaded rear axle group mass (kg).',
      long: 'Gross operating mass carried on the rear axle group when fully laden. Includes tare and payload. A warning is shown if limits are exceeded.',
   },
   'f-tare-mass': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Front Tare Mass',
      short: 'Unladen front axle mass (kg).',
      long: 'Weight of the front axle group with no payload, including fuel and accessories. Used to determine payload split and suspension setup.',
   },
   'r-tare-mass': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Rear Tare Mass',
      short: 'Unladen rear axle mass (kg).',
      long: 'Rear axle group mass with no payload, used in CoG calculations and mass split logic.',
   },
   'f-payload-mass': {
      section: 'Axle Loads',
      inputType: 'calculated',
      title: 'Front Payload Mass',
      short: 'Mass of payload carried by each axle group.',
      long: 'Derived from gross mass minus tare mass for axle group. ',
   },
   'r-payload-mass': {
      section: 'Axle Loads',
      inputType: 'calculated',
      title: 'Rear Payload Mass',
      short: 'Mass of payload carried by each axle group.',
      long: 'Derived from gross mass minus tare mass for axle group. ',
   },
   'f-unsprung-mass': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Front Unsprung Mass (per axle)',
      short: 'Mass of axle and wheel hardware.',
      long: 'Non-suspended mass of each front axle including wheels, hubs, and brake components. Entered per axle.',
   },
   'r-unsprung-mass': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Rear Unsprung Mass (per axle)',
      short: 'Unsprung mass of rear axle components.',
      long: 'Non-suspended mass of each rear axle including wheels, hubs, and brake components. Entered per axle.',
   },
   'f-unsprung-cg-height': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Front Unsprung Mass - CoG Height',
      short: 'CoG height of front axle components (m).',
      long: 'Vertical distance from ground to the centre of gravity of unsprung components on the front axle group, typically axle height - approximately half the loaded rolling diameter of the tyre.',
   },
   'r-unsprung-cg-height': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Rear Unsprung Mass - CoG Height',
      short: 'CoG height of rear axle components (m).',
      long: 'Vertical distance from ground to the centre of gravity of unsprung components on the rear axle group, typically axle height - approximately half the loaded rolling diameter of the tyre.',
   },
   'sprung-cg-height': {
      section: 'Axle Loads',
      inputType: 'user',
      title: 'Sprung Tare Mass CoG Height',
      short: 'The vertical height for the CoG of body/chassis (tare components).',
      long: 'The vertical centre of gravity of the sprung portion of the tare mass (excluding axles and wheels). Only override if precise measurement is available.',
   },
   'sprung-tare-mass': {
      section: 'Axle Loads',
      inputType: 'calculated',
      title: 'Total Sprung Tare Mass',
      short: 'The total of all tare masses less the unsprung masses.',
      long: 'Calculated as total tare mass minus unsprung mass for both axle groups. Represents the weight of the chassis and body excluding axles and wheels.',
   },

   // === Payload Geometry ===
   'deck-style': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Deck Style',
      short: 'Deck shape: Standard, Sloping, or Step.',
      long: `
    <p>Specifies the vertical geometry of the vehicle deck:</p>
    <ul>
      <li><strong>Standard</strong>: flat deck with uniform height</li>
      <li><strong>Sloping</strong>: angled deck; input front/rear heights</li>
      <li><strong>Step Deck</strong>: two discrete levels; requires deck heights and lengths. Weighted average CoG is used in step deck calculations.</li>
    </ul>
  `,
      figures: ['/img/slopingdeck-diagram.gif', '/img/stepdeck-diagram.gif'],
   },
   'load-bed-height': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Bed Height',
      short: 'Deck floor height from ground.',
      long: 'Vertical distance from the ground to the load bed in laden condition on a standard deck. Used to estimate payload CoG in “Mixed Freight” and “Uniform Density” modes.',
   },
   'load-height-max': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Maximum Load Height',
      short: 'Max load height.',
      long: 'Maximum height of the payload above ground level when laden. Used for CoG estimation with standard decks.',
   },
   'load-height-front': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Height (Front)',
      short: 'Max payload/body height at front (sloping deck only).',
      long: 'Maximum height of the payload above ground level when laden. Used for CoG estimation with sloping decks. The load height is average of front and rear load heights.',
   },
   'load-bed-height-front': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Bed Height (Front)',
      short: 'Front-end deck height above ground (sloping deck only).',
      long: 'The height of the deck floor above ground level when laden. Used for CoG estimation with sloping decks.  The load bed height is average of front and rear load bed heights.',
   },
   'load-height-rear': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Height (Rear)',
      short: 'Max payload/body height at rear. (sloping deck only).',
      long: 'Maximum height of the payload above ground level when laden. Used for CoG estimation with sloping decks.  The load height is average of front and rear load heights.',
   },
   'load-bed-height-rear': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Bed Height (Rear)',
      short: 'Rear-end deck height above ground (sloping deck only).',
      long: 'The height of the deck floor at rear above ground level when laden. Used for CoG estimation with sloping decks. The load bed height is average of front and rear load bed heights.',
   },
   'deck1-height': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Bed Height (Front Step)',
      short: 'Front (first) step deck height above ground (step deck only).',
      long: 'The height of the first deck floor above ground level when laden. Used for CoG estimation with step decks, load bed height is weighted average of front and rear load bed heights.',
   },
   'deck1-length': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Bed Length (Front)',
      short: 'Length of the first level deck (step deck only).',
      long: 'The length of the first deck floor above ground level when laden. Used for CoG estimation with step decks.',
   },
   'deck2-height': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Bed Height (Rear Step)',
      short: 'Rear (second) step deck height above ground (step deck only).',
      long: 'The height of the second deck floor above ground level when laden. Used for CoG estimation with step decks, load bed height is weighted average of front and rear load bed heights.',
   },
   'deck2-length': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Load Bed Length (Rear)',
      short: 'Length of the second level deck (step deck only).',
      long: 'The length of the second deck floor above ground level when laden. Used for CoG estimation with step decks.',
   },
   'deck-load-height-max': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Maximum Load Height (Step Deck)',
      short: 'Max load height above step decks.',
      long: 'Maximum height of the payload above ground level when laden for step deck vehicle. Used for CoG estimation with step decks, assumes same load height front and rear decks.',
   },
   'load-category': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Payload Distribution',
      short: 'Load profile distribution assumption.',
      long: `
               <p>Determines how payload CoG height is estimated based on the payload distribution:</p>
               <ul>
            <li><strong>Mixed Freight</strong>: assumes that 70% of the payload mass is in the bottom half of the load space and 30% in the top half.
               When averaged, this places the payload CoG at about <em>40% of the distance between the load bed and the load height</em>
               (closer to the deck than the midpoint). This covers typical container trucks, curtainsiders and van body vehicles.</li>

            <li><strong>Uniform Density</strong>: assumes the payload density is evenly distributed from the deck to the top of the load.
               The payload CoG is therefore taken as the <em>midpoint between the load bed and the load height</em>.
               Examples include logs, liquid tankers and tippers. </li>

            <li><strong>Custom</strong>: for loads not covered by the above categories, the user must directly enter the payload CoG height.
               This applies to large indivisible items (e.g. heavy machinery) or special shapes such as triangular hoppers.</li>
            </ul>
  `,
   },
   'payload-cg-height': {
      section: 'Payload Geometry',
      inputType: 'user',
      title: 'Payload Centre of Gravity Height',
      short: 'Payload CoG height from ground (m).',
      long: 'When a Standard deck with Custom payload distribution is selected - A user-entered height from ground to centre of gravity of payload.',
   },

   // === Suspension ===
   suspensionTypeFront: {
      section: 'Suspension',
      inputType: 'user',
      title: 'Front Suspension Type',
      short: 'Generic or user-defined front suspension.',
      long: 'Select a front suspension configuration or enter specific values manually. Manufacturer data is recommended when available.',
   },
   suspensionTypeRear: {
      section: 'Suspension',
      inputType: 'user',
      title: 'Rear Suspension Type',
      short: 'Generic or custom rear suspension.',
      long: 'Select a rear suspension configuration or enter specific values manually. Manufacturer data is recommended when available.',
   },
   'f-spring-spacing': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Front Spring Spacing',
      short: 'Distance between spring mounts.',
      long: 'Distance between mounting points of the front suspension springs, measured at the axle. Used with spring stiffness to compute roll stiffness.',
   },
   'r-spring-spacing': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Rear Spring Spacing',
      short: 'Spacing of rear suspension springs.',
      long: 'Distance between mounting points of the rear suspension springs, measured at the axle. Used with spring stiffness to compute roll stiffness.',
   },
   'f-spring-stiffness': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Front Spring Stiffness (per side)',
      short: 'Vertical stiffness of front springs (N/m) each side.',
      long: 'Vertical stiffness of each front suspension spring. Two springs per axle are assumed. Used to compute spring roll stiffness.',
   },
   'r-spring-stiffness': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Rear Spring Stiffness (per side)',
      short: 'Vertical stiffness of rear springs (N/m) each side.',
      long: 'Vertical stiffness of each rear suspension spring. Two springs per axle are assumed. Used to compute spring roll stiffness.',
   },
   'f-roll-stiffness': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Front Roll Stiffness (total per axle)',
      short: 'Total front roll stiffness (Nm/rad) for axle.',
      long: 'Total front axle group roll stiffness, including contributions from springs and any auxiliary components (e.g. anti-roll bars). Increasing spring stiffness without adjusting total will reduce auxiliary stiffness.',
   },
   'r-roll-stiffness': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Rear Roll Stiffness (total per axle)',
      short: 'Total rear roll stiffness (Nm/rad) for axle.',
      long: 'Total rear axle group roll stiffness, including contributions from springs and any auxiliary components (e.g. anti-roll bars). Increasing spring stiffness without adjusting total will reduce auxiliary stiffness.',
   },
   'f-aux-roll-stiffness': {
      section: 'Suspension',
      inputType: 'calculated',
      title: 'Front Auxiliary roll stiffness (calculated)',
      short: 'Additional roll resistance supplied by anti-roll bars or other auxiliary hardware per axle.',
      long: 'The calculated contribution to front roll stiffness from components other than the springs, such as anti-roll bars. Positive values increase roll resistance.',
   },
   'r-aux-roll-stiffness': {
      section: 'Suspension',
      inputType: 'calculated',
      title: 'Rear Auxiliary roll stiffness (calculated)',
      short: 'Additional roll resistance supplied by anti-roll bars or other auxiliary hardware per axle.',
      long: 'The calculated contribution to front roll stiffness from components other than the springs, such as anti-roll bars. Positive values increase roll resistance.',
   },
   'f-roll-center-height': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Front Roll Centre Height',
      short: 'Height of the roll centre from axle centre. Positive is above, negative is below.',
      long: 'Height of the suspension roll centre above or below the front axle. Must be sourced from the manufacturer — do not estimate.',
   },
   'r-roll-center-height': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Rear Roll Centre Height',
      short: 'Height of the roll centre from axle centre. Positive is above, negative is below.',
      long: 'Height of the suspension roll centre above or below the rear axle. Defines roll moment arm. Manufacturer data is required — do not estimate.',
   },
   'f-lash': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Front Lash',
      short: 'The suspension’s free-play range from compression into tension. (front).',
      long: 'Free movement of the suspension in rebound before spring force is applied. Lash is typically higher in air suspensions due to lack of tension force. Value entered is at the axle.',
   },
   'r-lash': {
      section: 'Suspension',
      inputType: 'user',
      title: 'Rear Lash',
      short: 'The suspension’s free-play range from compression into tension. (rear).',
      long: 'Free movement of the suspension in rebound before spring force is applied. Lash is typically higher in air suspensions due to lack of tension force. Value entered is at the axle.',
   },
};
